import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";
import { Loader2 } from "lucide-react";
import LandingHeroSection from "./components/LandingHeroSection";
// TODO: Implement About section for future use
// import LandingAboutSection from "./components/LandingAboutSection";
import LandingServicesSection from "./components/LandingServicesSection";
import LandingProductsServicesSection from "./components/LandingProductsServicesSection";
import LandingInfoSection from "./components/LandingInfoSection";
import LandingAppointmentSection from "./components/LandingAppointmentSection";
import LandingGallerySection from "./components/LandingGallerySection";
import LandingReviewsSection from "./components/LandingReviewsSection";
import LandingPostsSection from "./components/LandingPostsSection";
import LandingBlogsSection from "./components/LandingBlogsSection";
import LandingSocialSection from "./components/LandingSocialSection";
import LandingFooter from "./components/LandingFooter";
import LandingNavbar from "./components/LandingNavbar";
import LandingWhatsAppButton from "./components/LandingWhatsAppButton";
import { generateThemeCSS } from "@/utils/themeGenerator";
import { useMicrositeAnalytics } from "@/hooks/useMicrositeAnalytics";
import { useThirdPartyAnalytics } from "@/hooks/useThirdPartyAnalytics";
import type { AnalyticsConfig } from "@/utils/analyticsScriptBuilder";
import {
  LANDING_PAGE_SECTIONS,
  getVisibleSections,
  type SectionAvailabilityData,
} from "./landingPageSectionsConfig";
import "./landing-theme.css";
import "./landing-theme-categories.css";
import "./landing-theme-universal.css";
import "./landing-theme-palettes.css";

interface LandingPageData {
  // Subdomain config
  subdomain_config_id?: string;
  subdomain: string;
  theme_config: any;
  seo_config: any;
  // Location data from gmb_locations
  location: {
    id: string;
    name: string;
    address: string;
    formatted_address: string;
    primary_phone: string;
    website_uri: string;
    latitude: number;
    longitude: number;
    rating: number;
    review_count: number;
    average_rating?: number;
    reviews_count?: number;
    photos: any[];
    regular_hours: any;
    business_hours: any;
    maps_url: string;
    primary_category: string;
    additional_categories: any[];
    auto_detected_services: any[];
    description: string;
    media_items?: {
      logo?: {
        googleUrl?: string;
        sourceUrl?: string;
        thumbnailUrl?: string;
      };
      coverPhoto?: {
        googleUrl?: string;
        sourceUrl?: string;
        thumbnailUrl?: string;
      };
      photos?: any[];
    };
  };
  // Workspace data for branding
  workspace: {
    id: string;
    name: string;
    brand_name: string;
    logo_url: string;
    business_description: string;
    website: string;
    facebook_url: string;
    instagram_url: string;
    twitter_url: string;
    linkedin_url: string;
    youtube_url: string;
    agency_id?: string;
  };
  // Agency branding (for white-label workspaces)
  agencyBranding?: {
    brandName: string;
    logoUrl?: string;
    primaryColor?: string;
    websiteUrl?: string;
  };
  // Page customizations (optional)
  customizations?: {
    hero_content?: any;
    about_content?: any;
    services_content?: any;
    products_services_content?: any;
    gallery_content?: any;
    reviews_content?: any;
    posts_content?: any;
    blogs_content?: any;
    social_content?: any;
    contact_content?: any;
    appointment_content?: any;
    show_hero?: boolean;
    show_about?: boolean;
    show_services?: boolean;
    show_products_services?: boolean;
    show_gallery?: boolean;
    show_reviews?: boolean;
    show_posts?: boolean;
    show_blogs?: boolean;
    show_social?: boolean;
    show_contact?: boolean;
    show_appointment?: boolean;
    custom_css?: string;
    custom_scripts?: string;
    analytics_config?: AnalyticsConfig;
  };
}

interface LandingPageProps {
  activeSection?: string;
  // Edit mode props (for Microsites Manager)
  editMode?: boolean;
  locationId?: string;
  workspaceId?: string;
  // SSR mode props (for server-side rendering)
  ssrData?: {
    landingData: LandingPageData;
    hasPublishedPosts: boolean;
    hasReviews: boolean;
    hasBlogs: boolean;
    hasProductsServices: boolean;
    finalLocationId: string;
    finalWorkspaceId: string;
    postsData?: any[];
    reviewsData?: any[];
    blogsData?: any[];
    productsData?: any[];
  };
  subdomain?: string;
}

const LandingPage = ({
  activeSection,
  editMode = false,
  locationId,
  workspaceId,
  ssrData,
  subdomain: subdomainProp,
}: LandingPageProps) => {
  const params = useParams<{ subdomain?: string }>();

  // Extract subdomain from props OR URL params OR hostname
  const getSubdomain = () => {
    // First check if passed as prop
    if (subdomainProp) {
      return subdomainProp;
    }

    // Then check URL params (for /subdomain/:subdomain route)
    if (params.subdomain) {
      return params.subdomain;
    }

    // Finally check hostname (for subdomain.localhost or subdomain.my-domain.com)
    if (typeof window !== "undefined") {
      const hostname = window.location.hostname;
      const parts = hostname.split(".");

      // For subdomain.localhost or subdomain.my-domain.com
      if (parts.length >= 2 && parts[0] !== "localhost" && parts[0] !== "www") {
        return parts[0];
      }
    }

    return null;
  };

  const subdomain = getSubdomain();

  console.log("=== LandingPage Init ===");
  console.log("Subdomain from prop:", subdomainProp);
  console.log("Subdomain from params:", params.subdomain);
  console.log("Final subdomain:", subdomain);
  console.log("Active section:", activeSection);
  console.log("SSR Data received:", {
    hasSSRData: !!ssrData,
    postsDataCount: ssrData?.postsData?.length || 0,
    reviewsDataCount: ssrData?.reviewsData?.length || 0,
    hasPublishedPosts: ssrData?.hasPublishedPosts,
    hasReviews: ssrData?.hasReviews
  });
  console.log("=======================");

  // Initialize state from SSR data if available
  const [landingData, setLandingData] = useState<LandingPageData | null>(
    ssrData?.landingData || null
  );
  const [loading, setLoading] = useState(!ssrData);
  const [error, setError] = useState<string | null>(null);
  const [hasPublishedPosts, setHasPublishedPosts] = useState(
    ssrData?.hasPublishedPosts || false
  );
  const [hasReviews, setHasReviews] = useState(ssrData?.hasReviews || false);
  const [hasBlogs, setHasBlogs] = useState(ssrData?.hasBlogs || false);
  const [hasProductsServices, setHasProductsServices] = useState(
    ssrData?.hasProductsServices || false
  );
  const [hasServices, setHasServices] = useState(false);
  const [hasGallery, setHasGallery] = useState(false);
  const [hasSocial, setHasSocial] = useState(false);
  const [hasAppointment, setHasAppointment] = useState(true); // Default to true unless explicitly disabled

  // Store SSR pre-fetched data
  const [ssrPostsData] = useState(ssrData?.postsData || null);
  const [ssrReviewsData] = useState(ssrData?.reviewsData || null);
  const [ssrBlogsData] = useState(ssrData?.blogsData || null);
  const [ssrProductsData] = useState(ssrData?.productsData || null);

  // Listen for live theme updates from editor panel (via postMessage)
  useEffect(() => {
    if (!editMode) return;

    const handleThemeUpdate = (event: MessageEvent) => {
      // Ignore React DevTools and browser extension messages
      if (
        event.data?.source === "react-devtools-content-script" ||
        event.data?.source === "react-devtools-bridge" ||
        event.data?.source === "react-devtools-inject-backend"
      ) {
        return;
      }

      if (event.data && event.data.type === "UPDATE_THEME") {
        const newTheme = event.data.theme;
        const newStyles = event.data.styles;
        const customThemeClasses = event.data.customThemeClasses;

        setLandingData((prev) => {
          if (!prev) return prev;

          return {
            ...prev,
            theme_config: {
              ...prev.theme_config,
              theme: newTheme,
              styles: newStyles,
              ...(customThemeClasses ? { customThemeClasses } : {}),
            },
          };
        });
      }
    };

    window.addEventListener("message", handleThemeUpdate);
    return () => window.removeEventListener("message", handleThemeUpdate);
  }, [editMode]);

  useEffect(() => {
    // Check if preview mode is enabled via URL parameter - forces fresh fetch
    // Accepts any truthy value (preview=1, preview=true, etc.) for cache-busting
    const urlParams = new URLSearchParams(window.location.search);
    const isPreviewMode = !!urlParams.get('preview');

    if (ssrData) {
      // SSR already pre-fetched the data; component state was initialized
      // from it at line 205. In preview mode we DO want fresh data, but
      // we must let React finish hydrating against the SSR-rendered HTML
      // FIRST — otherwise the synchronous fetch + setState causes a
      // hydration mismatch (FLY-LOCAL-BOOST-B on agency microsites).
      // Defer the refresh past the current reconciliation tick.
      if (isPreviewMode) {
        const refetchHandle = setTimeout(() => fetchLandingPageData(), 0);
        return () => clearTimeout(refetchHandle);
      }
      return;
    }

    // Check if we have SSR pre-fetched data from window (for client-side hydration)
    if (typeof window !== "undefined" && (window as any).__SSR_DATA__) {
      const windowSsrData = (window as any).__SSR_DATA__;

      // Set all state from SSR data
      setLandingData(windowSsrData.landingData);
      setHasPublishedPosts(windowSsrData.hasPublishedPosts);
      setHasReviews(windowSsrData.hasReviews);
      setHasBlogs(windowSsrData.hasBlogs);
      setLoading(false);

      // Clear SSR data after using it
      delete (window as any).__SSR_DATA__;

      // Same deferred-refetch rule as the ssrData prop path above.
      if (isPreviewMode) {
        const refetchHandle = setTimeout(() => fetchLandingPageData(), 0);
        return () => clearTimeout(refetchHandle);
      }
    } else {
      // No SSR data, fetch fresh data
      fetchLandingPageData();
    }
  }, [subdomain, locationId, workspaceId, ssrData]);

  // Helper function to filter out empty and duplicate services
  // Defined here so it can be used in useEffect and generateSections
  const getCleanedServices = (services: any[]) => {
    if (!services || !Array.isArray(services)) return [];

    // Filter out empty services (no title or display_name)
    const nonEmptyServices = services.filter(service => {
      const title = service?.title || service?.display_name || service?.displayName || service?.name || '';
      return title && title.trim().length > 0;
    });

    // Remove duplicates based on title (case-insensitive)
    const uniqueServices = nonEmptyServices.filter((service, index, self) => {
      const title = (service?.title || service?.display_name || service?.displayName || service?.name || '').toLowerCase().trim();
      return index === self.findIndex(s => {
        const sTitle = (s?.title || s?.display_name || s?.displayName || s?.name || '').toLowerCase().trim();
        return sTitle === title;
      });
    });

    return uniqueServices;
  };

  // Set hasServices, hasGallery, hasSocial, hasAppointment when landingData changes
  useEffect(() => {
    if (!landingData) return;

    // Check services availability
    const cleanedServicesForCheck = getCleanedServices(landingData.location?.auto_detected_services || []);
    setHasServices(cleanedServicesForCheck.length > 0);

    // Check gallery availability (check both photos and media_items.photos)
    const hasPhotos = (landingData.location?.photos?.length || 0) > 0;
    const hasMediaItemsPhotos = (landingData.location?.media_items?.photos?.length || 0) > 0;
    setHasGallery(hasPhotos || hasMediaItemsPhotos);

    // Check social links availability
    const hasSocialLinks = !!(
      landingData.workspace?.facebook_url ||
      landingData.workspace?.instagram_url ||
      landingData.workspace?.twitter_url ||
      landingData.workspace?.linkedin_url ||
      landingData.workspace?.youtube_url
    );
    setHasSocial(hasSocialLinks);

    // Check appointment availability (default true unless explicitly disabled)
    const hasAppointmentEnabled = landingData.customizations?.show_appointment !== false;
    setHasAppointment(hasAppointmentEnabled);
  }, [landingData]);

  // Initialize analytics hook (always call hook with default values if data not ready)
  const analytics = useMicrositeAnalytics({
    subdomainConfigId: landingData?.subdomain_config_id || '',
    locationId: landingData?.location?.id,
    workspaceId: landingData?.workspace?.id || '',
  });

  // Inject third-party analytics scripts (GA4, GTM, Meta Pixel, etc.)
  // Skips injection in edit mode and when scripts are already present from SSR
  useThirdPartyAnalytics(
    landingData?.customizations?.analytics_config || null,
    !!editMode
  );

  // Check if analytics has valid required parameters
  const hasValidAnalytics =
    !editMode &&
    landingData?.subdomain_config_id &&
    landingData?.workspace?.id;

  // Track page view when landing data is loaded (only in public mode, not edit mode)
  useEffect(() => {
    if (hasValidAnalytics && analytics) {
      console.log('[Analytics] Tracking page view for subdomain:', landingData.subdomain);
      analytics.trackPageView();
    }
  }, [hasValidAnalytics, landingData, analytics]);

  const fetchLandingPageData = async () => {
    try {
      setLoading(true);
      setError(null);

      let finalLocationId: string;
      let finalWorkspaceId: string;
      let subdomainConfigData: any = null;

      // EDIT MODE: Fetch by locationId directly
      if (editMode && locationId && workspaceId) {

        // Fetch location data
        const { data: location, error: locationError } = await supabase
          .from("gmb_locations")
          .select("*")
          .eq("id", locationId)
          .single();

        if (locationError || !location) {
          throw new Error("Location not found");
        }

        // Fetch workspace data
        const { data: workspace, error: workspaceError } = await supabase
          .from("workspaces")
          .select("*")
          .eq("id", workspaceId)
          .single();

        if (workspaceError || !workspace) {
          throw new Error("Workspace not found");
        }

        // Fetch agency branding if workspace belongs to an agency
        let agencyBranding = undefined;
        if (workspace.agency_id) {
          const { data: agency } = await supabase
            .from("agencies")
            .select("brand_name, logo_url, primary_color, website_url")
            .eq("id", workspace.agency_id)
            .single();

          if (agency) {
            agencyBranding = {
              brandName: agency.brand_name,
              logoUrl: agency.logo_url,
              primaryColor: agency.primary_color,
              websiteUrl: agency.website_url,
            };
          }
        }

        // Fetch subdomain config (might not exist yet)
        const { data: subdomainConfig } = await supabase
          .from("subdomain_configs")
          .select("*")
          .eq("location_id", locationId)
          .eq("subdomain_type", "landing")
          .maybeSingle();

        // Fetch customizations if they exist (optional)
        const { data: customizations } = await supabase
          .from("subdomains_landing_setting")
          .select("*")
          .eq("location_id", locationId)
          .maybeSingle();

        // Build theme config, defaulting to agency colors if not customized
        const baseTheme = subdomainConfig?.theme_config || { theme: "light", styles: {} };
        const themeWithAgencyDefaults = {
          ...baseTheme,
          // Use agency colors as defaults if theme doesn't have custom colors
          primaryColor: baseTheme.primaryColor || agencyBranding?.primaryColor || undefined,
          // secondaryColor could be derived from primary if agency only provides primary
        };

        setLandingData({
          subdomain_config_id: subdomainConfig?.id,
          subdomain: subdomainConfig?.subdomain || "",
          theme_config: themeWithAgencyDefaults,
          seo_config: subdomainConfig?.seo_config || {
            title: "",
            description: "",
            keywords: [],
          },
          location: location,
          workspace: workspace,
          agencyBranding: agencyBranding,
          customizations: customizations || undefined,
        });

        finalLocationId = locationId;
        finalWorkspaceId = workspaceId;
      }
      // PUBLIC MODE: Fetch by subdomain
      else {
        const { data: subdomainConfig, error: configError } = await supabase
          .from("subdomain_configs")
          .select(
            `
            *,
            location:gmb_locations!location_id(*),
            workspace:workspaces!workspace_id(*)
          `
          )
          .eq("subdomain", subdomain)
          .eq("subdomain_type", "landing")
          .eq("is_active", true)
          .single();

        if (configError || !subdomainConfig) {
          throw new Error("Landing page not found");
        }

        // Fetch agency branding if workspace belongs to an agency
        let publicAgencyBranding = undefined;
        if (subdomainConfig.workspace?.agency_id) {
          const { data: agency } = await supabase
            .from("agencies")
            .select("brand_name, logo_url, primary_color, website_url")
            .eq("id", subdomainConfig.workspace.agency_id)
            .single();

          if (agency) {
            publicAgencyBranding = {
              brandName: agency.brand_name,
              logoUrl: agency.logo_url,
              primaryColor: agency.primary_color,
              websiteUrl: agency.website_url,
            };
          }
        }

        // Fetch customizations if they exist (optional)
        const { data: customizations } = await supabase
          .from("subdomains_landing_setting")
          .select("*")
          .eq("location_id", subdomainConfig.location_id)
          .maybeSingle();

        // Build theme config, defaulting to agency colors if not customized
        const publicBaseTheme = subdomainConfig.theme_config || { theme: "light", styles: {} };
        const publicThemeWithAgencyDefaults = {
          ...publicBaseTheme,
          // Use agency colors as defaults if theme doesn't have custom colors
          primaryColor: publicBaseTheme.primaryColor || publicAgencyBranding?.primaryColor || undefined,
        };

        setLandingData({
          subdomain_config_id: subdomainConfig.id,
          subdomain: subdomainConfig.subdomain,
          theme_config: publicThemeWithAgencyDefaults,
          seo_config: subdomainConfig.seo_config,
          location: subdomainConfig.location,
          workspace: subdomainConfig.workspace,
          agencyBranding: publicAgencyBranding,
          customizations: customizations || undefined,
        });

        finalLocationId = subdomainConfig.location_id;
        finalWorkspaceId = subdomainConfig.workspace_id;
      }

      // Check if there are published posts with actual content for this location
      console.log("=== CHECKING PUBLISHED POSTS AVAILABILITY ===");
      console.log("Location ID for posts check:", finalLocationId);

      // First get published post locations
      const { data: postLocations, error: postError } = await supabase
        .from("gmb_post_locations")
        .select("post_id, metadata")
        .eq("location_id", finalLocationId)
        .eq("status", "published")
        .limit(6);

      if (!postError && postLocations && postLocations.length > 0) {
        // Try to get content for these posts
        const postIds = postLocations.map((p) => p.post_id);
        const { data: postContent } = await supabase
          .from("gmb_post_content")
          .select("id, summary")
          .in("id", postIds);

        // Check if we have posts with actual content
        let postsWithContent = [];

        if (postContent && postContent.length > 0) {
          // Use content from gmb_post_content table
          postsWithContent = postContent.filter(
            (content) => content.summary && content.summary.trim().length > 0
          );
        } else {
          // Fallback to metadata, but only if it has real content
          postsWithContent = postLocations.filter((loc) => {
            const summary = loc.metadata?.summary || loc.metadata?.text || "";
            return summary && summary.trim().length > 0;
          });
        }

        setHasPublishedPosts(postsWithContent.length > 0);
        console.log("Posts with content found:", postsWithContent.length);
        console.log(
          "Has published posts with content:",
          postsWithContent.length > 0
        );
      } else {
        setHasPublishedPosts(false);
        console.log("No published posts found or error:", postError);
      }

      // Check if there are reviews for this location
      console.log("=== CHECKING REVIEWS AVAILABILITY ===");
      console.log("Location ID for reviews check:", finalLocationId);

      const { count: reviewCount, error: reviewError } = await supabase
        .from("gmb_reviews")
        .select("*", { count: "exact", head: true })
        .eq("location_id", finalLocationId);

      console.log("Review count query error:", reviewError);
      console.log("Review count result:", reviewCount);

      const hasReviewsResult = (reviewCount || 0) > 0;
      setHasReviews(hasReviewsResult);
      console.log("Has reviews:", hasReviewsResult);
      console.log("=== REVIEWS AVAILABILITY CHECK COMPLETE ===");

      // Check if there are blogs for this location
      console.log("=== CHECKING BLOGS AVAILABILITY ===");
      console.log("Location ID for blogs check:", finalLocationId);

      // First get blog locations
      const { data: blogLocations, error: blogLocError } = await supabase
        .from("blog_locations")
        .select("blog_id")
        .eq("location_id", finalLocationId);

      console.log("Blog locations error:", blogLocError);
      console.log("Blog locations found:", blogLocations?.length || 0);

      let hasBlogsResult = false;
      if (blogLocations && blogLocations.length > 0) {
        const blogIds = blogLocations.map((bl) => bl.blog_id);
        const { count: blogCount, error: blogError } = await supabase
          .from("blogs")
          .select("*", { count: "exact", head: true })
          .in("id", blogIds)
          .eq("status", "published");

        console.log("Published blogs count error:", blogError);
        console.log("Published blogs count:", blogCount);
        hasBlogsResult = (blogCount || 0) > 0;
      }

      setHasBlogs(hasBlogsResult);
      console.log("Has blogs:", hasBlogsResult);
      console.log("=== BLOGS AVAILABILITY CHECK COMPLETE ===");

      // Check if there are products/services for this workspace
      const { count: productCount } = await supabase
        .from("products_services")
        .select("*", { count: "exact", head: true })
        .eq("workspace_id", finalWorkspaceId);
      setHasProductsServices((productCount || 0) > 0);

      // Check if there are gallery photos (check both photos and media_items.photos)
      const { data: locationWithPhotos } = await supabase
        .from("gmb_locations")
        .select("photos, media_items")
        .eq("id", finalLocationId)
        .single();
      const hasGalleryPhotos = (locationWithPhotos?.photos?.length || 0) > 0;
      const hasMediaItemsPhotos = (locationWithPhotos?.media_items?.photos?.length || 0) > 0;
      setHasGallery(hasGalleryPhotos || hasMediaItemsPhotos);

      // Check if there are services (use location data we already fetched)
      let locationForServices;
      if (editMode && locationId) {
        // In edit mode, we already have the location from earlier query
        const { data: loc } = await supabase
          .from("gmb_locations")
          .select("auto_detected_services")
          .eq("id", finalLocationId)
          .single();
        locationForServices = loc;
      } else {
        // In public mode, we already have location in landingData
        // But we need to fetch it here as we're in the middle of the fetch function
        const { data: loc } = await supabase
          .from("gmb_locations")
          .select("auto_detected_services")
          .eq("id", finalLocationId)
          .single();
        locationForServices = loc;
      }
      const cleanedServicesForCheck = getCleanedServices(locationForServices?.auto_detected_services || []);
      const hasServicesAvailable = cleanedServicesForCheck.length > 0;
      setHasServices(hasServicesAvailable);

      // Check if there are social links
      const { data: workspaceWithSocial } = await supabase
        .from("workspaces")
        .select(
          "facebook_url, instagram_url, twitter_url, linkedin_url, youtube_url"
        )
        .eq("id", finalWorkspaceId)
        .single();

      const hasSocialLinks = !!(
        workspaceWithSocial?.facebook_url ||
        workspaceWithSocial?.instagram_url ||
        workspaceWithSocial?.twitter_url ||
        workspaceWithSocial?.linkedin_url ||
        workspaceWithSocial?.youtube_url
      );
      setHasSocial(hasSocialLinks);

      // Check if appointment section is enabled (true by default unless explicitly disabled)
      const { data: appointmentSettings } = await supabase
        .from("subdomains_landing_setting")
        .select("show_appointment")
        .eq("location_id", finalLocationId)
        .maybeSingle();

      const hasAppointmentEnabled =
        appointmentSettings?.show_appointment !== false;
      setHasAppointment(hasAppointmentEnabled);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (error || !landingData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Landing Page Not Found
          </h1>
          <p className="text-gray-600">
            The landing page you're looking for doesn't exist.
          </p>
        </div>
      </div>
    );
  }

  const { theme_config, seo_config, location, workspace, customizations, agencyBranding } =
    landingData;

  const effectivePhone = customizations?.contact_content?.phone_override || location.primary_phone;
  const effectiveEmail = customizations?.contact_content?.email_override || null;

  // Build theme class names from config
  const buildThemeClasses = () => {
    // Support custom theme classes from Website Builder (category/universal themes + palettes)
    if (theme_config?.customThemeClasses) {
      return theme_config.customThemeClasses;
    }

    const classes = [`theme-${theme_config?.theme || "light"}`];

    // Add style modifier classes
    if (theme_config?.styles) {
      Object.values(theme_config.styles).forEach((styleValue) => {
        if (styleValue) {
          classes.push(styleValue as string);
        }
      });
    }

    return classes.join(" ");
  };

  // Debug logging
  console.log("Location data:", location);
  console.log("Media items:", location?.media_items);
  console.log("Workspace data:", workspace);

  // Extract logo and cover photo from media_items
  // Try sourceUrl first (Cloudinary), then thumbnailUrl, then googleUrl
  const logoUrl =
    location?.media_items?.logo?.sourceUrl ||
    location?.media_items?.logo?.thumbnailUrl ||
    location?.media_items?.logo?.googleUrl ||
    workspace?.logo_url;

  // For cover photo, use full image (googleUrl or sourceUrl) instead of thumbnail
  const coverPhotoUrl =
    location?.media_items?.coverPhoto?.googleUrl ||
    location?.media_items?.coverPhoto?.sourceUrl ||
    location?.media_items?.coverPhoto?.thumbnailUrl;

  console.log("Logo URL extracted:", logoUrl);
  console.log("Cover Photo URL extracted:", coverPhotoUrl);

  // Generate sections based on available data using centralized config
  const generateSections = () => {
    // Prepare availability data for config checks
    const availabilityData: SectionAvailabilityData = {
      location,
      workspace,
      customizations,
      hasReviews,
      hasPublishedPosts,
      hasBlogs,
      hasProductsServices,
      getCleanedServices,
    };

    // Get visible sections from config
    const visibleSectionConfigs = getVisibleSections(availabilityData);

    // Map config to section objects for rendering
    return visibleSectionConfigs.map((sectionConfig) => {
      // Base section object
      const section: any = {
        id: sectionConfig.id,
        type: sectionConfig.type,
        title: sectionConfig.title,
      };

      // Add section-specific content
      switch (sectionConfig.type) {
        case 'hero':
          section.content = customizations?.hero_content || {};
          break;
        case 'services':
          const cleanedServices = getCleanedServices(location?.auto_detected_services || []);
          section.content = customizations?.services_content || { services: cleanedServices };
          break;
        case 'reviews':
          section.content = customizations?.reviews_content || {};
          break;
        case 'gallery':
          section.content = customizations?.gallery_content || { photos: location.photos };
          break;
        case 'posts':
          section.content = customizations?.posts_content || {};
          break;
        case 'blogs':
          section.content = customizations?.blogs_content || {};
          break;
        case 'products-services':
          section.content = customizations?.products_services_content || {};
          break;
        case 'contact-info':
          section.content = customizations?.contact_content || {};
          break;
        case 'appointment':
          section.content = customizations?.appointment_content || {};
          break;
        case 'social':
          section.content = customizations?.social_content || {};
          break;
        default:
          section.content = {};
      }

      return section;
    });
  };

  const allSections = generateSections();

  // Filter sections based on activeSection parameter
  const getSectionsToRender = () => {
    if (!activeSection) {
      // Home page - show all sections
      return allSections;
    }

    // Map URL section names to section types
    const sectionMap: { [key: string]: string } = {
      'contact-us': 'contact-info',
      'appointment': 'appointment',
      'services': 'services',
      'products': 'products-services',
      'products-services': 'products-services',
      'gallery': 'gallery',
      'reviews': 'reviews',
      'posts': 'posts',
      'updates': 'posts',
      'blogs': 'blogs',
      'blog': 'blogs',
      'social': 'social'
    };

    const targetSection = sectionMap[activeSection] || activeSection;

    // For specific sections, only return that section
    return allSections.filter((s) => s.type === targetSection);
  };

  const sections = getSectionsToRender();

  console.log("=== LandingPage Rendering ===");
  console.log("Active section:", activeSection);
  console.log("Total sections generated:", allSections.length);
  console.log(
    "All section types:",
    allSections.map((s) => s.type)
  );
  console.log("Sections to render:", sections.length);
  console.log(
    "Section types to render:",
    sections.map((s) => s.type)
  );
  console.log("SSR data available:", {
    hasPublishedPosts,
    hasReviews,
    hasBlogs,
    hasProductsServices,
    postsDataCount: ssrPostsData?.length || 0,
    reviewsDataCount: ssrReviewsData?.length || 0,
  });
  console.log("============================");

  // Prepare business info from GMB location data
  // Helper function to check if hours object has actual data
  const hasValidHours = (hours: any) => {
    if (!hours) return false;
    if (typeof hours !== 'object') return false;
    // Check if it's an empty object
    if (Object.keys(hours).length === 0) return false;
    // Check if it has periods array with data
    if (hours.periods && Array.isArray(hours.periods) && hours.periods.length > 0) return true;
    // Check if it has day keys (MONDAY, TUESDAY, etc.)
    const dayKeys = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
    return dayKeys.some(day => hours[day]);
  };

  // Get business hours with priority order: regular_hours > business_hours > metadata.regularHours
  // This matches the priority used in LocationEdit.tsx
  const getBusinessHours = () => {
    if (hasValidHours(location.regular_hours)) return location.regular_hours;
    if (hasValidHours(location.business_hours)) return location.business_hours;
    if (hasValidHours(location.metadata?.regularHours)) return location.metadata.regularHours;
    return null;
  };

  const businessInfo = {
    phone: effectivePhone,
    email: effectiveEmail,
    address: location.address,
    formatted_address: location.formatted_address,
    latitude: location.latitude,
    longitude: location.longitude,
    website: location.website_uri,
    hours: getBusinessHours(),
    rating: location.rating,
    review_count: location.review_count,
    services: getCleanedServices(location.auto_detected_services || []),
    social_links: {
      facebook: workspace?.facebook_url,
      instagram: workspace?.instagram_url,
      twitter: workspace?.twitter_url,
      linkedin: workspace?.linkedin_url,
      youtube: workspace?.youtube_url,
    },
  };

  return (
    <>
      <Helmet>
        <title>
          {seo_config?.title ||
            `${location.name} - ${
              workspace?.brand_name || workspace?.name || "Business"
            }`}
        </title>
        <meta
          name="description"
          content={
            seo_config?.description ||
            location.description ||
            `Visit ${
              location.name
            } for ${location.primary_category?.toLowerCase()} services.`
          }
        />
        <meta
          name="keywords"
          content={
            seo_config?.keywords?.join(", ") ||
            [
              location.primary_category,
              ...(location.additional_categories || []),
            ].join(", ")
          }
        />
        <link rel="canonical" href={`https://${subdomain}.fly-social.com`} />
      </Helmet>

      <div className={`subdomain-landing-root ${buildThemeClasses()} `}>
        {/* Apply custom CSS if provided */}
        {customizations?.custom_css && (
          <style
            dangerouslySetInnerHTML={{ __html: customizations.custom_css }}
          />
        )}

        <LandingNavbar
          businessName={location.name}
          logoUrl={logoUrl}
          pages={sections}
          theme={theme_config}
          hasPublishedPosts={hasPublishedPosts}
          hasReviews={hasReviews}
          hasBlogs={hasBlogs}
          hasProductsServices={hasProductsServices}
          hasServices={hasServices}
          hasGallery={hasGallery}
          hasSocial={hasSocial}
          hasAppointment={hasAppointment}
          phoneNumber={effectivePhone}
          editMode={editMode}
          onTrackPhoneClick={hasValidAnalytics ? analytics.trackPhoneClick : undefined}
          location={location}
          workspace={workspace}
          getCleanedServices={getCleanedServices}
          customizations={customizations}
        />

        <main>
          {/* Render sections dynamically */}
          {sections.map((section) => {
            const sectionProps = {
              content: section.content,
              theme: theme_config,
              businessInfo: businessInfo,
              location: location,
              workspace: workspace,
            };

            switch (section.type) {
              case "hero":
                return (
                  <LandingHeroSection
                    key={section.id}
                    businessName={location.name}
                    tagline={
                      section.content?.tagline ||
                      seo_config?.description ||
                      location.description
                    }
                    theme={theme_config}
                    rating={location.average_rating || location.rating}
                    reviewCount={
                      location.reviews_count || location.review_count
                    }
                    phoneNumber={effectivePhone}
                    heroContent={{
                      ...section.content,
                      backgroundImage: section.content?.backgroundImage || coverPhotoUrl,
                    }}
                    onTrackPhoneClick={hasValidAnalytics ? analytics.trackPhoneClick : undefined}
                  />
                );
              case "services":
                // Get cleaned services for SSR data
                const cleanedServicesForSSR = getCleanedServices(location?.auto_detected_services || []);
                return (
                  <LandingServicesSection
                    key={section.id}
                    {...sectionProps}
                    ssrServicesData={cleanedServicesForSSR}
                    isStandalonePage={activeSection === "services"}
                  />
                );
              case "gallery":
                return (
                  <LandingGallerySection
                    key={section.id}
                    {...sectionProps}
                    isStandalonePage={activeSection === "gallery"}
                  />
                );
              case "reviews":
                console.log("=== RENDERING REVIEWS SECTION ===");
                console.log("Section props for reviews:", sectionProps);
                return (
                  <LandingReviewsSection
                    key={section.id}
                    {...sectionProps}
                    isStandalonePage={activeSection === "reviews"}
                    ssrReviewsData={ssrReviewsData}
                  />
                );
              case "posts":
                return (
                  <LandingPostsSection
                    key={section.id}
                    locationId={location.id}
                    theme={theme_config}
                    content={section.content}
                    isStandalonePage={activeSection === "posts"}
                    ssrPostsData={ssrPostsData}
                  />
                );
              case "blogs":
                return (
                  <LandingBlogsSection
                    key={section.id}
                    locationId={location.id}
                    theme={theme_config}
                    content={section.content}
                    isStandalonePage={activeSection === "blogs"}
                    ssrBlogsData={ssrBlogsData}
                  />
                );
              case "products-services":
                return (
                  <LandingProductsServicesSection
                    key={section.id}
                    workspaceId={workspace.id}
                    theme={theme_config}
                    content={section.content}
                    isStandalonePage={activeSection === "products-services"}
                    ssrProductsData={ssrProductsData}
                    onTrackProductView={hasValidAnalytics ? analytics.trackProductView : undefined}
                  />
                );
              case "contact-info":
                return (
                  <LandingInfoSection
                    key={section.id}
                    {...sectionProps}
                    onTrackPhoneClick={hasValidAnalytics ? analytics.trackPhoneClick : undefined}
                    onTrackEmailClick={hasValidAnalytics ? analytics.trackEmailClick : undefined}
                    onTrackDirectionsClick={hasValidAnalytics ? analytics.trackDirectionsClick : undefined}
                  />
                );
              case "appointment":
                return (
                  <LandingAppointmentSection
                    key={section.id}
                    {...sectionProps}
                    onTrackBooking={hasValidAnalytics ? analytics.trackAppointmentBooking : undefined}
                  />
                );
              case "social":
                return (
                  <LandingSocialSection
                    key={section.id}
                    {...sectionProps}
                    onTrackSocialClick={hasValidAnalytics ? analytics.trackSocialClick : undefined}
                  />
                );
              default:
                return null;
            }
          })}
        </main>

        <LandingFooter
          businessName={location.name}
          businessInfo={businessInfo}
          theme={theme_config}
          workspace={workspace}
          location={location}
          agencyBranding={agencyBranding}
        />

        {/* WhatsApp Chat Button */}
        <LandingWhatsAppButton
          phoneNumber={effectivePhone}
          businessName={location.name}
          onTrackClick={hasValidAnalytics ? analytics.trackWhatsAppClick : undefined}
        />

        {/* Apply custom scripts if provided */}
        {customizations?.custom_scripts && (
          <script
            dangerouslySetInnerHTML={{ __html: customizations.custom_scripts }}
          />
        )}
      </div>
    </>
  );
};

export default LandingPage;
